import {gql} from '@apollo/client';

import BenchlingConfig from 'BenchlingConfig';
import {doQuerySafe} from 'graphql-client/ApolloWrappers';
import {getFragmentWrapper} from 'graphql-client/declarative-translations/external/getFragmentWrapper';
import {isInaccessible} from 'graphql-client/InaccessibleInfo';
import {getRestrictedSampleUsersDisplayText} from 'inventory/sample-status/RestrictedSampleUsersTextUtil';
import {SampleControlsFeatureLevel} from 'inventory/sample-status/SampleControlsFeatureLevel';
import {ColumnGroup} from 'notebook/structured-tables/columns/ColumnDefinitions';
import {
  StructuredTable,
  StructuredTableColumnGroups,
  StructuredTableRow,
} from 'notebook/structured-tables/StructuredTableConstants';
import {
  SampleOwnersQuery,
  SampleOwnersQueryVariables,
} from 'notebook/structured-tables/util/__generated/SampleOwnersQuery';
import {TableColumnType} from 'notebook/tables/constants/TableColumnConstants';
import {EvaluatedTable} from 'notebook/tables/excess/EvaluationConstants';
import {
  getPartyIdsFromTypedValue,
  getStorableIdsFromTypedValue,
} from 'notebook/tables/typed-values/DataTypes';
import {ButtonStyle} from 'ui-library/buttons/Button';
import * as modals from 'util/Modals';

export const SAMPLE_OWNERS_QUERY = gql`
  query SampleOwnersQuery($sampleIds: [StrictString!]!) @skipAudit {
    storables(ids: $sampleIds) {
      ... on StorableInterface {
        id
      }
      ... on ContainerNode {
        id
        sampleRoles {
          id
          sampleOwners {
            ... on PartyInterface {
              id
            }
          }
        }
      }
      ... on InaccessibleInfoNode {
        ...InaccessibleInfoGraphQL
      }
    }
  }
  ${getFragmentWrapper('InaccessibleInfo').fragment}
`;

function getPartyIdsBySampleIdFromStructuredTable<TTableMetadata, TRowMetadata>({
  structuredTable,
  slicedStructuredTable,
  computedTable,
  partyColumnGroup,
  sampleColumnGroup,
}: {
  structuredTable: StructuredTable<TTableMetadata, TRowMetadata>;
  slicedStructuredTable?: Array<StructuredTableRow<TRowMetadata>>;
  computedTable: EvaluatedTable;
  partyColumnGroup: ColumnGroup | null;
  sampleColumnGroup: ColumnGroup | null;
}): {[sampleId: string]: Array<string>} | null {
  const partyColumnIndex = structuredTable.columns.findIndex(
    ({columnType, columnGroup}) =>
      columnType === TableColumnType.PARTIES && (partyColumnGroup == null || columnGroup === partyColumnGroup)
  );
  const sampleColumnIndex = structuredTable.columns.findIndex(
    ({columnType, columnGroup}) =>
      columnType === TableColumnType.CONTAINERS &&
      (sampleColumnGroup == null || columnGroup === sampleColumnGroup)
  );
  if (partyColumnIndex < 0 || sampleColumnIndex < 0) {
    return null;
  }

  return computedTable.reduce((byId: {[sampleId: string]: Array<string>}, row, rowIndex) => {
    let rowsToUse;
    if (slicedStructuredTable) {
      rowsToUse = slicedStructuredTable;
    } else {
      rowsToUse = structuredTable.rows;
    }

    if (!rowsToUse[rowIndex].isFinalized) {
      const sampleTypedValue =
        row[sampleColumnIndex].value ?? rowsToUse[rowIndex].cells[sampleColumnIndex].typedValue;
      const sampleIds = getStorableIdsFromTypedValue(sampleTypedValue);
      if (sampleIds.length !== 1) {
        return byId;
      }
      const partiesTypedValue =
        row[partyColumnIndex].value ?? rowsToUse[rowIndex].cells[partyColumnIndex].typedValue;
      const partyIds = getPartyIdsFromTypedValue(partiesTypedValue);
      return {...byId, [sampleIds[0]]: partyIds};
    }
    return byId;
  }, {});
}

async function getCurrentSampleOwnerIdsBySampleId(
  sampleIds: Array<string>
): Promise<{[sampleId: string]: Array<string>}> {
  const result = await doQuerySafe<SampleOwnersQuery, SampleOwnersQueryVariables>(
    {query: SAMPLE_OWNERS_QUERY},
    {
      variables: {sampleIds},
    }
  );

  const samples = result.storables.flatMap((s) =>
    isInaccessible(s) || s.__typename !== 'ContainerNode' ? [] : [s]
  );
  // eslint-disable-next-line benchlint/no-spread-reduce
  return samples.reduce(
    (byId: {[sampleId: string]: Array<string>}, sample) => ({
      ...byId,
      [sample.id]: sample.sampleRoles.sampleOwners.map(({id}) => id),
    }),
    {}
  );
}

export async function isClearingSampleOwners<TTableMetadata, TRowMetadata>({
  structuredTable,
  slicedStructuredTable,
  computedTable,
  targetSampleColumnGroup,
}: {
  structuredTable: StructuredTable<TTableMetadata, TRowMetadata>;
  slicedStructuredTable?: Array<StructuredTableRow<TRowMetadata>>;
  computedTable: EvaluatedTable;
  targetSampleColumnGroup: ColumnGroup | null;
}): Promise<boolean> {
  const newOwnerIdsBySampleId = getPartyIdsBySampleIdFromStructuredTable({
    structuredTable,
    slicedStructuredTable,
    computedTable,
    partyColumnGroup: StructuredTableColumnGroups.SAMPLE_OWNERS,
    sampleColumnGroup: targetSampleColumnGroup,
  });
  if (newOwnerIdsBySampleId == null) {
    return false;
  }
  const sampleIds = Object.keys(newOwnerIdsBySampleId);
  const oldOwnerIdsBySampleId = await getCurrentSampleOwnerIdsBySampleId(sampleIds);

  return !sampleIds.every(
    (sampleId) =>
      newOwnerIdsBySampleId[sampleId].length > 0 ||
      oldOwnerIdsBySampleId[sampleId] == null ||
      oldOwnerIdsBySampleId[sampleId].length === 0
  );
}

export async function confirmIfClearingSampleOwners<TTableMetadata, TRowMetadata>({
  structuredTable,
  slicedStructuredTable,
  computedTable,
  targetSampleColumnGroup,
}: {
  structuredTable: StructuredTable<TTableMetadata, TRowMetadata>;
  slicedStructuredTable?: Array<StructuredTableRow<TRowMetadata>>;
  computedTable: EvaluatedTable;
  targetSampleColumnGroup: ColumnGroup | null;
}): Promise<boolean> {
  if (
    (BenchlingConfig.INVENTORY_ENABLE_SAMPLE_CONTROL_FEATURES as SampleControlsFeatureLevel) !==
    SampleControlsFeatureLevel.V1
  ) {
    return true;
  }
  const needConfirmClearSampleOwners = await isClearingSampleOwners({
    structuredTable,
    slicedStructuredTable,
    computedTable,
    targetSampleColumnGroup,
  });
  const proceedWithSubmit = needConfirmClearSampleOwners
    ? await modals.confirm(
        'Remove all sample owners?',
        `Some items will have all sample owners removed, which will remove all ${getRestrictedSampleUsersDisplayText()} and allow anyone to use these containers.`,
        ButtonStyle.NEGATIVE,
        {
          acceptText: 'Remove',
          enableRedesign: true,
          promiseCompatible: true,
        }
      )
    : true;
  return Boolean(proceedWithSubmit);
}
